import { useEffect, useState } from "react";
import { Layout, Menu, Breadcrumb, Dropdown, Avatar, ConfigProvider, Button } from "antd";
import type { MenuProps } from "antd";
import {
  HomeOutlined,
  AppstoreOutlined,
  CloudServerOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import userManager from "@/hooks/oidcConfig";
import { Activity } from "lucide-react";

const { Header, Sider, Content } = Layout;
const SIDER_WIDTH = 220;
const SIDER_COLLAPSED_WIDTH = 80;
const SIDER_STORAGE_KEY = "report_layout_sidebar_collapsed";

// 左侧菜单配置：概览中心、业务模块、企业数字化
const siderMenuItems: MenuProps["items"] = [
  { key: "/", icon: <HomeOutlined />, label: "概览中心" },
  { key: "/business", icon: <AppstoreOutlined />, label: "业务模块" },
  { key: "/enterprise", icon: <CloudServerOutlined />, label: "企业数字化" },
];

// 根据路径得到当前选中的菜单 key（业务子路由也高亮「业务模块」）
function getSelectedKey(pathname: string): string {
  if (pathname === "/" || pathname === "") return "/";
  if (pathname === "/business" || pathname.startsWith("/sku-calendar") || pathname.startsWith("/logistics")
    || pathname.startsWith("/InventoryForecast") || pathname.startsWith("/OnSaleProducts")
    || pathname.startsWith("/OnSaleSpuProducts") || pathname.startsWith("/forecast-map-status")
    || pathname.startsWith("/report/")) return "/business";
  if (pathname === "/enterprise") return "/enterprise";
  return pathname;
}

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const pathname = location.pathname;
  const selectedKey = getSelectedKey(pathname);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SIDER_STORAGE_KEY) === "true";
  });

  useEffect(() => {
    localStorage.setItem(SIDER_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const handleLogout = () => {
    const identityServerBase = import.meta.env.VITE_OIDC_SERVER_URL;
    const client_id = import.meta.env.VITE_OIDC_CLIENT_ID;
    const name = `oidc.user:${identityServerBase}:${client_id}`;
    const token = localStorage.getItem("access_token");
    if (token) {
      Object.keys(localStorage).forEach((key) => {
        if (key !== name) localStorage.removeItem(key);
      });
      userManager.signoutRedirect();
    }
  };

  const userMenuItems: MenuProps["items"] = [
    // {
    //   key: "profile",
    //   icon: <UserOutlined />,
    //   label: "个人资料",
    //   onClick: () => navigate("/profile"),
    // },
    // {
    //   key: "settings",
    //   icon: <SettingOutlined />,
    //   label: "系统设置",
    //   onClick: () => navigate("/settings"),
    // },
    // {
    //   key: "reports",
    //   icon: <FileTextOutlined />,
    //   label: "报表导出",
    //   onClick: () => navigate("/reports"),
    // },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
      danger: true,
      onClick: handleLogout,
    },
  ];

  // 面包屑：根据路径生成
  const getBreadcrumbItems = () => {
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length === 0) return [{ title: "概览中心" }];
    const map: Record<string, string> = {
      business: "业务模块",
      enterprise: "企业数字化",
      "sku-calendar": "SPU排产日历",
      logistics: "物流监控",
      InventoryForecast: "备货预测",
      OnSaleProducts: "在售商品情况",
      OnSaleSpuProducts: "在售商品分类",
      "forecast-map-status": "预测MAPE情况",
      report: "报表",
    };
    return [
      { title: map[segments[0]] || "概览中心" },
      ...segments.slice(1).map((s) => ({ title: map[s] || s })),
    ];
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Layout: {
            siderBg: "#001529",
            headerBg: "#fff",
            bodyBg: "#f5f6f8",
          },
          Menu: {
            darkItemBg: "#001529",
            darkSubMenuItemBg: "transparent",
          },
        },
      }}
    >
      <Layout style={{ minHeight: "100vh" }}>
        <Sider
          theme="dark"
          width={SIDER_WIDTH}
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          collapsed={collapsed}
          trigger={null}
          breakpoint="lg"
          className="tech-sidebar-glow"
          style={{
            overflow: "auto",
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            background: "linear-gradient(180deg, #0a1628 0%, #001529 50%, #000d1a 100%)",
          }}
        >
          <div style={{ height: 64, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "0 16px" }}>
            <span className="tech-logo-glow">
              <Activity style={{ width: 28, height: 28, color: "#60a5fa" }} />
            </span>
            {!collapsed && (
              <span style={{ color: "#f1f5f9", fontWeight: 600, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", letterSpacing: "0.02em" }}>
                智能报表中心
              </span>
            )}
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            items={siderMenuItems}
            onClick={({ key }) => navigate(key)}
            style={{ borderRight: 0, marginTop: 8 }}
          />
        </Sider>
        <Layout
          style={{
            marginLeft: collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "margin-left 0.2s ease",
          }}
        >
          <Header
            className="tech-header-glass"
            style={{
              flexShrink: 0,
              padding: "0 24px",
              height: 64,
              minHeight: 64,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "sticky",
              top: 0,
              zIndex: 50,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((prev) => !prev)}
                aria-label={collapsed ? "展开左侧菜单" : "收起左侧菜单"}
              />
              <Breadcrumb items={getBreadcrumbItems()} style={{ margin: 0 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
                <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: "#1890ff" }}>
                    {(user?.name || "用户").charAt(0).toUpperCase()}
                  </Avatar>
                  <span style={{ fontSize: 14, color: "#262626" }}>{user?.name || "用户"}</span>
                </div>
              </Dropdown>
            </div>
          </Header>
          <Content
            className="tech-content-bg"
            style={{
              flex: 1,
              margin: "24px",
              padding: 24,
              borderRadius: 12,
              overflow: "auto",
              minHeight: 0,
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}
